import { useMemo } from 'react'
import { useWindowSize } from '@react-hookz/web'

import { breakpoints } from '../utils/breakpoints'

type BreakpointsChecks = {
  mediaXs?: boolean
  mediaSm?: boolean
  mediaSmd?: boolean
  mediaMd?: boolean
  mediaBmd?: boolean
  mediaMlg?: boolean
  mediaLg?: boolean
  mediaXl?: boolean

  isMobile?: boolean
  isTablet?: boolean
  isDesktop?: boolean
}

/**
 * Custom hook to get the current breakpoint.
 *
 * The `useBreakpoints` hook listens for window resize events and returns the current breakpoint.
 * It helps in making responsive design decisions within your React components.
 *
 * @returns {string} The current breakpoint (e.g., 'xs', 'sm', 'md', 'lg', 'xl').
 */
export const useBreakpoints = (): BreakpointsChecks => {
  const { width } = useWindowSize(undefined, true)

  const mediaXs = true
  const mediaSm = width >= breakpoints.sm
  const mediaSmd = width >= breakpoints.smd
  const mediaMd = width >= breakpoints.md
  const mediaBmd = width >= breakpoints.bmd
  const mediaMlg = width >= breakpoints.mlg
  const mediaLg = width >= breakpoints.lg
  const mediaXl = width >= breakpoints.xl

  const isMobile = width < breakpoints.md
  const isTablet = width >= breakpoints.md && width < breakpoints.lg
  const isDesktop = width >= breakpoints.lg

  const memoizedBreakpoints = useMemo(
    () => ({
      mediaXs,
      mediaSm,
      mediaSmd,
      mediaMd,
      mediaBmd,
      mediaMlg,
      mediaLg,
      mediaXl,
      isMobile,
      isTablet,
      isDesktop,
    }),
    [
      mediaSm,
      mediaSmd,
      mediaMd,
      mediaBmd,
      mediaMlg,
      mediaLg,
      mediaXl,
      isMobile,
      isTablet,
      isDesktop,
    ]
  )

  return memoizedBreakpoints
}
